/** Reports → Timesheets: the time consultants logged in the Tools app over the range. */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel, ShowAllButton } from "./ui";
import { useActiveConsultants, useTeamFilter, useTeamOf, useTimesheets } from "./data";
import type { TimeEntry } from "./data";
import { initials } from "./ConsultantsScreen";
import TimesheetEntries, { fmtHours } from "./TimesheetEntries";
import { weekdaysBetween } from "./reportRange";
import type { ReportRange } from "./reportRange";


type LoggedDay = { date: string; entries: TimeEntry[]; minutes: number };
type Row = { email: string; name: string; team: string; days: LoggedDay[]; entries: number; minutes: number };

const Stat = ({ value, label, tip }: { value: string | number; label: string; tip?: string }) => (
    <Box title={tip} sx={{ background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "8px", padding: "10px 11px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
        <Typography sx={{ fontSize: "10.5px", color: T.muted, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
);

const BarRow = ({ label, value, max }: { label: string; value: number; max: number }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 0" }}>
        <Typography sx={{ fontSize: "11.5px", color: T.muted, width: "170px", flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Typography>
        <Box sx={{ flexGrow: 1, height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
            <Box sx={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, height: "100%", background: T.purple }} />
        </Box>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: T.ink2, width: "50px", textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{fmtHours(value)}</Typography>
    </Box>
);

const head = { fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted } as const;
const num = { fontSize: "12.5px", color: T.ink2, textAlign: "right", fontVariantNumeric: "tabular-nums" } as const;
const GRID = "minmax(0, 1.6fr) repeat(4, minmax(70px, 0.6fr))";

const TimesheetTab = ({ range, team = "AllTeams", consultant = "AllConsultants", onOpen }: { range: ReportRange; team?: string; consultant?: string; onOpen?: (email: string, name: string, teamName: string) => void }) => {
    const teamOf = useTeamOf();
    const roster = useActiveConsultants();
    const inTeam = useTeamFilter(team);
    const time = useTimesheets(range.from, range.to);
    const [open, setOpen] = useState<string | null>(null);
    const [showAll, setShowAll] = useState(false);
    const [showAllActivity, setShowAllActivity] = useState(false);
    const [showAllAdvisory, setShowAllAdvisory] = useState(false);
    const weekdays = weekdaysBetween(range.from, range.to).length;

    const rows = useMemo<Row[]>(
        () =>
            roster.active
                .filter((c) => inTeam(c.email))
                .filter((c) => consultant === "AllConsultants" || (c.email || "").toLowerCase() === consultant.toLowerCase())
                .map((c) => {
                    const logs = time.byEmail.get((c.email || "").toLowerCase());
                    const days = [...(logs?.entries() ?? [])]
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([date, entries]) => ({ date, entries, minutes: entries.reduce((n, e) => n + e.minutes, 0) }));
                    return {
                        email: c.email,
                        name: c.name,
                        team: teamOf(c.email),
                        days,
                        entries: days.reduce((n, d) => n + d.entries.length, 0),
                        minutes: days.reduce((n, d) => n + d.minutes, 0),
                    };
                })
                .sort((a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name)),
        [roster.active, inTeam, consultant, time.byEmail, teamOf],
    );

    const totals = useMemo(() => {
        const byActivity = new Map<string, number>();
        const byAdvisory = new Map<string, number>();
        rows.forEach((r) => r.days.forEach((d) => d.entries.forEach((e) => {
            const k = e.activityType || "not set";
            byActivity.set(k, (byActivity.get(k) || 0) + e.minutes);
            if (e.advisoryType) byAdvisory.set(e.advisoryType, (byAdvisory.get(e.advisoryType) || 0) + e.minutes);
        })));
        const days = rows.reduce((n, r) => n + r.days.length, 0);
        const minutes = rows.reduce((n, r) => n + r.minutes, 0);
        return {
            entries: rows.reduce((n, r) => n + r.entries, 0),
            days,
            minutes,
            perDay: days ? minutes / days : 0,
            loggers: rows.filter((r) => r.entries > 0).length,
            activities: [...byActivity.entries()].sort((a, b) => b[1] - a[1]),
            advisories: [...byAdvisory.entries()].sort((a, b) => b[1] - a[1]),
        };
    }, [rows]);

    if (roster.isLoading || time.isLoading)
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: T.muted, padding: "24px 0" }}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: "12.5px" }}>Loading timesheets…</Typography>
            </Box>
        );
    if (time.isError) return <Typography sx={{ fontSize: "12.5px", color: T.red }}>Couldn't load timesheets for this range.</Typography>;

    const shown = showAll ? rows : rows.slice(0, 10);
    // Both charts show as many rows as the shorter one, so the panels line up.
    const chartLengths = [totals.activities.length, totals.advisories.length].filter((n) => n > 0);
    const chartRows = chartLengths.length === 2 ? Math.min(...chartLengths) : 10;
    const top = rows.slice(0, 5);
    const bottom = rows.slice(-5).reverse();
    const barMax = Math.max(1, ...rows.map((r) => r.minutes));
    const medal = ["🥇", "🥈", "🥉"];
    const rankRow = (r: Row, i: number, label: string, labelLarge: boolean) => (
        <Box
            key={r.email}
            onClick={onOpen ? () => onOpen(r.email, r.name, r.team) : undefined}
            title={onOpen ? `Open ${r.name}` : undefined}
            sx={{ display: "grid", gridTemplateColumns: "30px minmax(0, 1.3fr) minmax(0, 1fr) 110px", gap: "10px", alignItems: "center", padding: "8px 0", borderTop: i === 0 ? "none" : `1px solid ${T.borderSoft}`, ...(onOpen ? { cursor: "pointer", "&:hover": { background: T.rowTint } } : {}) }}
        >
            <Typography sx={{ fontSize: labelLarge ? "16px" : "12px", fontWeight: 800, color: T.muted, textAlign: "center" }}>{label}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>{initials(r.name)}</Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</Typography>
                    <Typography sx={{ fontSize: "10.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.team}</Typography>
                </Box>
            </Box>
            <Box sx={{ position: "relative", height: "12px", borderRadius: "4px", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                <Box sx={{ position: "absolute", top: "1px", bottom: "1px", left: 0, width: `${(r.minutes / barMax) * 100}%`, background: T.purple, borderRadius: "3px", transition: "width .3s" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: r.minutes ? T.ink2 : T.amber, fontVariantNumeric: "tabular-nums" }}>{r.minutes ? fmtHours(r.minutes) : "Nothing"}</Typography>
                <Typography sx={{ fontSize: "10.5px", color: T.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{r.days.length} days · {r.entries} entries</Typography>
            </Box>
        </Box>
    );
    const none = <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this range.</Typography>;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                <Stat value={`${totals.loggers} of ${rows.length}`} label="Consultants logging time" />
                <Stat value={totals.entries} label="Timesheet entries" />
                <Stat value={fmtHours(totals.minutes)} label="Time logged" />
                <Stat value={totals.days} label="Days with time logged" tip={`Person-days, out of ${weekdays} weekdays each`} />
                <Stat value={fmtHours(totals.perDay)} label="Average per logged day" />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                <Panel title="Most time logged" sub="top 5 in the range">
                    {top.length ? top.map((r, i) => rankRow(r, i, medal[i] ?? `#${i + 1}`, i < 3)) : none}
                </Panel>
                <Panel title="Least time logged" sub="bottom 5 in the range, least first">
                    {bottom.length ? bottom.map((r, i) => rankRow(r, i, `#${i + 1}`, false)) : none}
                </Panel>
            </Box>

            <Panel title="Time logged by consultant" sub={`most to least · click a row for each day · ${weekdays} weekdays in range`}>
                <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: "8px", padding: "0 0 8px", borderBottom: `1px solid ${T.border}` }}>
                    <Typography sx={head}>Consultant</Typography>
                    {["Days logged", "Entries", "Time", "Per day"].map((h) => <Typography key={h} sx={{ ...head, textAlign: "right" }}>{h}</Typography>)}
                </Box>
                {shown.map((r) => (
                    <Box key={r.email} sx={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                        <Box
                            onClick={r.entries ? () => setOpen((o) => (o === r.email ? null : r.email)) : undefined}
                            sx={{ display: "grid", gridTemplateColumns: GRID, gap: "8px", alignItems: "center", padding: "8px 0", ...(r.entries ? { cursor: "pointer", "&:hover": { background: T.rowTint } } : {}) }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                                <Box
                                    title={r.entries ? (open === r.email ? "Hide days" : "Show each day") : undefined}
                                    sx={{
                                        width: "24px",
                                        height: "24px",
                                        flexShrink: 0,
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "14px",
                                        fontWeight: 800,
                                        ...(r.entries
                                            ? { border: `1px solid ${open === r.email ? T.purple : T.border}`, color: open === r.email ? T.purple : T.muted, background: open === r.email ? T.purpleTint : T.card }
                                            : {}),
                                    }}
                                >
                                    {r.entries ? (open === r.email ? "▾" : "▸") : ""}
                                </Box>
                                <Box sx={{ width: "28px", height: "28px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                                    {initials(r.name)}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(r.email, r.name, r.team); } : undefined}
                                        title={onOpen ? `Open ${r.name}` : undefined}
                                        sx={{ fontSize: "13px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...(onOpen ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}) }}
                                    >
                                        {r.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: "10.5px", color: r.entries ? T.muted : T.amber }}>{r.entries ? r.team : "⚠ Nothing logged in range"}</Typography>
                                </Box>
                            </Box>
                            <Typography sx={num}>{r.days.length || "—"}</Typography>
                            <Typography sx={num}>{r.entries || "—"}</Typography>
                            <Typography sx={{ ...num, fontWeight: 700 }}>{fmtHours(r.minutes)}</Typography>
                            <Typography sx={num}>{r.days.length ? fmtHours(r.minutes / r.days.length) : "—"}</Typography>
                        </Box>
                        {open === r.email && (
                            <Box sx={{ padding: "6px 0 14px 70px" }}>
                                <TimesheetEntries entries={r.days.flatMap((d) => d.entries)} />
                            </Box>
                        )}
                    </Box>
                ))}
                {rows.length > 10 && <ShowAllButton expanded={showAll} total={rows.length} onToggle={() => setShowAll((v) => !v)} />}
            </Panel>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                <Panel title="Time by activity" sub="all timesheet entries in view">
                    {totals.activities.length === 0
                        ? <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing logged in this range.</Typography>
                        : (showAllActivity ? totals.activities : totals.activities.slice(0, chartRows)).map(([label, m]) => <BarRow key={label} label={label} value={m} max={totals.activities[0][1]} />)}
                    {totals.activities.length > chartRows && <ShowAllButton expanded={showAllActivity} total={totals.activities.length} onToggle={() => setShowAllActivity((v) => !v)} />}
                </Panel>
                <Panel title="Time by advisory type" sub="entries with an advisory type set">
                    {totals.advisories.length === 0
                        ? <Typography sx={{ fontSize: "12.5px", color: T.muted }}>No advisory types logged in this range.</Typography>
                        : (showAllAdvisory ? totals.advisories : totals.advisories.slice(0, chartRows)).map(([label, m]) => <BarRow key={label} label={label} value={m} max={totals.advisories[0][1]} />)}
                    {totals.advisories.length > chartRows && <ShowAllButton expanded={showAllAdvisory} total={totals.advisories.length} onToggle={() => setShowAllAdvisory((v) => !v)} />}
                </Panel>
            </Box>
        </Box>
    );
};

export default TimesheetTab;
